import {
  Box,
  Divider,
  Drawer,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Icon,
  IconButton,
  Text,
  useDisclosure,
} from '@chakra-ui/react'
import { ReactNode, useContext, useEffect, useState } from 'react'
import { IconType } from 'react-icons'
import {
  TbAppWindow,
  TbAppWindowFilled,
  TbBasket,
  TbBasketFilled,
  TbChartPie,
  TbChartPieFilled,
  TbLogout2,
  TbMapPin,
  TbMapPinFilled,
  TbMenu2,
  TbPackage,
  TbSettings,
  TbUser,
  TbUserFilled,
} from 'react-icons/tb'
import { Link, useLocation } from 'react-router-dom'
import { AuthContext } from '../../App'
import useMobile from '../../hooks/useMobile'
import { scrollbarStyle } from '../../theme/scrollbarStyles'
import AdminForceUpdate from '../modals/AdminForceUpdate'
import { Events, EventsFilled, NotificationFilled, Notifications, TbPackageFilled } from '../icons/icons'

const NavButton = (props: { label: string; path: string; icon: IconType; filledIcon: IconType; onClick?: () => void }) => {
  const location = useLocation()
  const isActive =
    location.pathname === props.path ||
    location.pathname === `${props.path}/` ||
    (location.pathname.includes(props.path) && props.path !== '/admin')

  return (
    <Link to={props.path} onClick={props.onClick}>
      <Flex alignItems="center" gap="10px" mb="30px">
        <Icon w="24px" h="24px" as={isActive ? props.filledIcon : props.icon} />
        <Text color="#171C22" fontSize={isActive ? 'lg' : 'md'} lineHeight="20px" fontWeight={isActive ? '600' : '500'}>
          {props.label}
        </Text>
      </Flex>
    </Link>
  )
}

const NavItems = ({ onForceUpdateOpen, onNavigate }: { onForceUpdateOpen: () => void; onNavigate?: () => void }) => {
  const { logout } = useContext(AuthContext)

  return (
    <>
      <Box flexGrow={1} overflowY="auto" overflowX="hidden" sx={scrollbarStyle}>
        <NavButton label="Analytics" path="/admin" icon={TbChartPie} filledIcon={TbChartPieFilled} onClick={onNavigate} />
        <NavButton
          label="Notifications"
          path="/admin/notifications"
          icon={Notifications}
          filledIcon={NotificationFilled}
          onClick={onNavigate}
        />
        <Divider mb="20px" />
        <NavButton label="Admins" path="/admin/admin" icon={TbUser} filledIcon={TbUserFilled} onClick={onNavigate} />
        <NavButton label="Hosts" path="/admin/hosts" icon={TbUser} filledIcon={TbUserFilled} onClick={onNavigate} />
        <NavButton label="Users" path="/admin/users" icon={TbUser} filledIcon={TbUserFilled} onClick={onNavigate} />
        <Divider mb="20px" />
        <NavButton label="Locations" path="/admin/venues" icon={TbMapPin} filledIcon={TbMapPinFilled} onClick={onNavigate} />
        <NavButton label="Events" path="/admin/events" icon={Events} filledIcon={EventsFilled} onClick={onNavigate} />
        <NavButton label="Packages" path="/admin/packages" icon={TbPackage} filledIcon={TbPackageFilled} onClick={onNavigate} />
        <Divider mb="20px" />
        <NavButton label="Carousel" path="/admin/promotions" icon={TbBasket} filledIcon={TbBasketFilled} onClick={onNavigate} />
        <NavButton label="Transactions" path="/admin/purchases" icon={TbAppWindow} filledIcon={TbAppWindowFilled} onClick={onNavigate} />
      </Box>
      <Box pt="16px">
        <Flex
          w="100%"
          mb="16px"
          justifyContent="left"
          alignItems="center"
          gap="10px"
          cursor="pointer"
          onClick={onForceUpdateOpen}
        >
          <Icon w="24px" h="24px" as={TbSettings} />
          <Text fontSize="lg" lineHeight="20px" fontWeight="500">
            Force Update
          </Text>
        </Flex>
        <Flex
          w="100%"
          justifyContent="left"
          alignItems="center"
          gap="10px"
          cursor="pointer"
          color="#FF3B30"
          onClick={() => logout()}
        >
          <Icon w="24px" h="24px" as={TbLogout2} />
          <Text fontSize="lg" lineHeight="20px" fontWeight="500">
            Log out
          </Text>
        </Flex>
      </Box>
    </>
  )
}

const AdminWrapper = ({ children }: { children: ReactNode }) => {
  const isMobile = useMobile(1000)
  const [shouldScroll, setShouldScroll] = useState(false)
  const { logout } = useContext(AuthContext)
  const { isOpen: isForceUpdateOpen, onOpen: onForceUpdateOpen, onClose: onForceUpdateClose } = useDisclosure()
  const { isOpen: isDrawerOpen, onOpen: onDrawerOpen, onClose: onDrawerClose } = useDisclosure()

  useEffect(() => {
    const handleResize = () => {
      const height = window.innerHeight
      setShouldScroll(height < 730)
    }

    handleResize()
    window.addEventListener('resize', handleResize)

    return () => window.removeEventListener('resize', handleResize)
  }, [])

  if (isMobile)
    return (
      <Flex h="100vh" w="100%" direction="column" overflow="hidden">
        <Flex
          h="56px"
          minH="56px"
          px="16px"
          alignItems="center"
          justifyContent="space-between"
          bg="white"
          borderBottom="1px solid #DCE2EA"
          zIndex={1000}
        >
          <IconButton
            aria-label="Open menu"
            icon={<Icon w="24px" h="24px" as={TbMenu2} />}
            variant="ghost"
            onClick={onDrawerOpen}
          />
          <Box
            bgImage="/logo_black.svg"
            userSelect="none"
            w="70px"
            h="28px"
            bgPos="center"
            bgSize="contain"
            bgRepeat="no-repeat"
          />
          <Box w="40px" />
        </Flex>
        <Box flexGrow={1} overflowY="auto" overflowX="hidden" sx={scrollbarStyle}>
          {children}
        </Box>
        <Drawer isOpen={isDrawerOpen} placement="left" onClose={onDrawerClose}>
          <DrawerOverlay />
          <DrawerContent maxW="260px" p="24px" pt="30px">
            <DrawerCloseButton />
            <Box
              bgImage="/logo_black.svg"
              userSelect="none"
              w="80px"
              h="33px"
              bgPos="center"
              bgSize="contain"
              bgRepeat="no-repeat"
              mb="30px"
            />
            <NavItems onForceUpdateOpen={onForceUpdateOpen} onNavigate={onDrawerClose} />
          </DrawerContent>
        </Drawer>
        <AdminForceUpdate isOpen={isForceUpdateOpen} onClose={onForceUpdateClose} />
      </Flex>
    )

  return (
    <Flex h="100vh" w="100%" overflow="hidden">
      <Box h="100%" w="235px" bg="white" p="30px" zIndex={1000}>
        <Box
          bgImage="/logo_black.svg"
          userSelect="none"
          w="80px"
          h="33px"
          bgPos="center"
          bgSize="contain"
          bgRepeat="no-repeat"
          mb="40px"
        />
        <Box overflowY={shouldScroll ? 'auto' : 'hidden'} overflowX="hidden" h="calc(100% - 100px)" sx={scrollbarStyle}>
          <NavButton label="Analytics" path="/admin" icon={TbChartPie} filledIcon={TbChartPieFilled} />
          <NavButton
            label="Notifications"
            path="/admin/notifications"
            icon={Notifications}
            filledIcon={NotificationFilled}
          />
          <Divider mb="20px" />
          <NavButton label="Admins" path="/admin/admin" icon={TbUser} filledIcon={TbUserFilled} />
          <NavButton label="Hosts" path="/admin/hosts" icon={TbUser} filledIcon={TbUserFilled} />
          <NavButton label="Users" path="/admin/users" icon={TbUser} filledIcon={TbUserFilled} />
          <Divider mb="20px" />

          <NavButton label="Locations" path="/admin/venues" icon={TbMapPin} filledIcon={TbMapPinFilled} />
          <NavButton label="Events" path="/admin/events" icon={Events} filledIcon={EventsFilled} />
          <NavButton label="Packages" path="/admin/packages" icon={TbPackage} filledIcon={TbPackageFilled} />

          <Divider mb="20px" />
          <NavButton label="Carousel" path="/admin/promotions" icon={TbBasket} filledIcon={TbBasketFilled} />
          <NavButton label="Transactions" path="/admin/purchases" icon={TbAppWindow} filledIcon={TbAppWindowFilled} />
        </Box>
        <Box pos="absolute" bottom="30px" w="175px">
          <Flex
            w="100%"
            mb="16px"
            justifyContent="left"
            alignItems="center"
            gap="10px"
            cursor="pointer"
            onClick={onForceUpdateOpen}
          >
            <Icon w="24px" h="24px" as={TbSettings} />

            <Text fontSize={'lg'} lineHeight="20px" fontWeight={'500'}>
              Force Update
            </Text>
          </Flex>
          <Flex
            w="100%"
            justifyContent="left"
            alignItems="center"
            gap="10px"
            cursor="pointer"
            color="#FF3B30"
            onClick={() => logout()}
          >
            <Icon w="24px" h="24px" as={TbLogout2} />

            <Text fontSize={'lg'} lineHeight="20px" fontWeight={'500'}>
              Log out
            </Text>
          </Flex>
        </Box>
      </Box>
      <Divider orientation="vertical" zIndex={1000} />
      <Box h="100%" w="calc(100% - 236px)" zIndex={1} overflowY="auto" overflowX="hidden" sx={scrollbarStyle}>
        {children}
      </Box>
      <AdminForceUpdate isOpen={isForceUpdateOpen} onClose={onForceUpdateClose} />
    </Flex>
  )
}

export default AdminWrapper
